import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney } from "@/lib/api";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  ArrowUp, ArrowDown, MapPin, TrendingUp, Building, Clock, Boxes,
  Search, Star, Loader2, X, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

export default function Markets() {
  const [popular, setPopular] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);

  const loadAll = useCallback(async () => {
    const [pop, saved] = await Promise.all([
      api.get("/markets").then((r) => r.data).catch(() => []),
      api.get("/markets/saved").then((r) => r.data).catch(() => []),
    ]);
    setPopular(pop);
    setPinned(saved);
    if (!active) setActive(saved[0] || pop[0] || null);
  }, [active]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const onSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const r = await api.get(`/markets/search?query=${encodeURIComponent(q)}`);
      const m = r.data?.market;
      if (m) {
        // Preserve the friendly name the user typed when available.
        const friendly = q.replace(/\s+/g, " ").trim();
        const enriched = {
          ...m,
          metro: /^\d{5}$/.test(friendly)
            ? `ZIP ${friendly}`
            : `${friendly} · ${m.zip}`,
        };
        setActive(enriched);
        try { document.getElementById("market-spotlight")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't find that market");
    } finally {
      setSearching(false);
    }
  };

  const isPinned = (zip) => pinned.some((p) => p.zip === zip);

  const togglePin = async (m) => {
    if (!m?.zip) {
      toast.error("Can't pin — ZIP code missing");
      return;
    }
    try {
      if (isPinned(m.zip)) {
        await api.delete(`/markets/saved/${m.zip}`);
        setPinned((p) => p.filter((x) => x.zip !== m.zip));
        toast.success("Unpinned");
      } else {
        await api.post(`/markets/saved`, { zip: m.zip, label: m.metro });
        toast.success(`Pinned ${m.metro || m.zip}`);
        // Re-pull to get the snapshot back
        const saved = await api.get("/markets/saved").then((r) => r.data).catch(() => []);
        setPinned(saved);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update");
    }
  };

  return (
    <div data-testid="markets-page">
      <div className="topbar">
        <div>
          <div className="crumb">Markets</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-1">
            Market Intelligence
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Search any U.S. market by city or ZIP — median price, days-on-market, inventory, rent-to-price ratio.
          </p>
        </div>
      </div>

      {/* Search bar */}
      <form
        onSubmit={onSearch}
        className="card p-3 mb-5 flex flex-col sm:flex-row gap-2"
        data-testid="markets-search-form"
      >
        <div className="flex-1 flex items-center gap-2 px-3 bg-slate-50 rounded-lg border border-[var(--line)] focus-within:border-[var(--ink)]">
          <Search size={16} className="text-[var(--ink-3)] shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search any U.S. market — "85008", "Phoenix, AZ", "Memphis"…'
            className="flex-1 bg-transparent border-0 py-2.5 text-sm focus:outline-none"
            data-testid="markets-search-input"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-[var(--ink-3)] hover:text-[var(--ink)] p-1"
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={searching || !query.trim()}
          className="btn-money sm:w-auto"
          data-testid="markets-search-btn"
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {searching ? "Searching…" : "Search market"}
        </button>
      </form>

      {/* Pinned markets row */}
      {pinned.length > 0 && (
        <section className="mb-5" data-testid="markets-pinned-section">
          <div className="flex items-center gap-2 mb-2">
            <Star size={14} className="text-amber-500 fill-amber-400" />
            <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[var(--ink-2)]">
              Your pinned markets
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {pinned.map((m) => (
              <MarketChip
                key={m.zip || m.saved_id}
                m={m}
                active={active?.zip === m.zip}
                pinned
                onClick={() => setActive(m)}
                onTogglePin={() => togglePin(m)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Popular markets row */}
      <section className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[var(--ink-3)]" />
          <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[var(--ink-2)]">
            Popular markets
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="markets-popular-list">
          {popular.map((m) => (
            <MarketChip
              key={m.zip || m.metro}
              m={m}
              active={active?.metro === m.metro}
              pinned={isPinned(m.zip)}
              onClick={() => setActive(m)}
              onTogglePin={() => togglePin(m)}
            />
          ))}
        </div>
      </section>

      {/* Spotlight */}
      {active && <Spotlight m={active} onTogglePin={togglePin} pinned={isPinned(active.zip)} />}
    </div>
  );
}

// ─── chip ──────────────────────────────────────────────────────────────────

function MarketChip({ m, active, pinned, onClick, onTogglePin }) {
  return (
    <div
      className={
        "group relative inline-flex flex-col gap-1 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all " +
        (active
          ? "border-[var(--ink)] bg-white shadow-sm"
          : "border-[var(--line)] hover:border-[var(--ink-3)] bg-white")
      }
      onClick={onClick}
      data-testid={`market-chip-${(m.zip || m.metro || "").toString().replace(/[^a-zA-Z0-9]/g, "_")}`}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin?.();
        }}
        className={
          "absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center transition-all " +
          (pinned
            ? "text-amber-500 opacity-100"
            : "text-[var(--ink-3)] opacity-0 group-hover:opacity-100 hover:text-amber-500")
        }
        aria-label={pinned ? "Unpin market" : "Pin market"}
        data-testid={`market-pin-btn-${m.zip || "x"}`}
      >
        <Star size={13} className={pinned ? "fill-amber-400" : ""} />
      </button>
      <div className="flex items-center gap-1.5 text-sm font-display font-bold pr-6">
        <MapPin size={12} className="text-[var(--ink-3)] shrink-0" />
        {m.metro || m.zip}
      </div>
      <div className="flex items-center gap-3 font-mono tabular-nums">
        <span className="text-xs text-[var(--ink-2)]">{fmtMoney(m.median_price)}</span>
        <span
          className={
            "text-xs flex items-center gap-0.5 font-semibold " +
            ((m.yoy_change ?? 0) >= 0 ? "text-emerald-700" : "text-rose-600")
          }
        >
          {(m.yoy_change ?? 0) >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
          {Math.abs(m.yoy_change ?? 0).toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

// ─── spotlight card ────────────────────────────────────────────────────────

function Spotlight({ m, pinned, onTogglePin }) {
  const liveSource = m.source === "rentcast";
  return (
    <div className="grid lg:grid-cols-2 gap-5" id="market-spotlight" data-testid="market-spotlight">
      <div className="card">
        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
          <div className="min-w-0">
            <div className="label-eyebrow flex items-center gap-1.5">
              Spotlight {liveSource && <span className="text-[10px] font-bold uppercase text-emerald-700">· Live</span>}
            </div>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tighter mt-1 truncate">
              {m.metro}
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => onTogglePin?.(m)}
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors " +
                (pinned
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-[var(--line)] hover:bg-slate-50 text-[var(--ink-2)]")
              }
              data-testid="spotlight-pin-btn"
            >
              <Star size={12} className={pinned ? "fill-amber-400 text-amber-500" : ""} />
              {pinned ? "Pinned" : "Pin market"}
            </button>
            <span
              className="chip"
              style={{
                background: m.yoy_change >= 0 ? "rgba(16,185,129,0.1)" : "rgba(231,111,81,0.12)",
                color: m.yoy_change >= 0 ? "var(--primary-dark)" : "#B91C1C",
                borderColor: m.yoy_change >= 0 ? "rgba(16,185,129,0.35)" : "rgba(231,111,81,0.35)",
                fontSize: 12, padding: "5px 10px",
              }}
            >
              YoY {m.yoy_change >= 0 ? "+" : ""}{(m.yoy_change ?? 0).toFixed(1)}%
            </span>
          </div>
        </div>

        <div className="h-64 -mx-2 mt-2">
          <ResponsiveContainer>
            <LineChart data={(m.history || []).map((v, i) => ({ month: i + 1, price: v }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="month" hide />
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <Tooltip
                formatter={(v) => fmtMoney(v)}
                labelFormatter={(l) => `Month ${l}`}
                contentStyle={{ borderRadius: 8, border: "1px solid var(--line)", fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="price"
                stroke={m.yoy_change >= 0 ? "var(--primary)" : "var(--warn)"}
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat
          icon={Building}
          label="Median price"
          value={fmtMoney(m.median_price)}
          sub="12-month trailing"
          testid="stat-median-price"
        />
        <Stat
          icon={Clock}
          label="Days on market"
          value={`${m.dom || 0}d`}
          sub={m.dom && m.dom > 35 ? "slow market" : m.dom > 0 ? "healthy" : "—"}
          testid="stat-dom"
        />
        <Stat
          icon={Boxes}
          label="Inventory"
          value={(m.inventory || 0).toLocaleString()}
          sub="active listings"
          testid="stat-inventory"
        />
        <Stat
          icon={TrendingUp}
          label="Rent growth"
          value={`${m.rent_growth >= 0 ? "+" : ""}${(m.rent_growth ?? 0).toFixed(1)}%`}
          sub={m.rent_to_price_pct ? `${m.rent_to_price_pct.toFixed(2)}% rent-to-price` : "12-mo YoY"}
          testid="stat-rent-growth"
        />
        {m.median_rent ? (
          <Stat
            icon={Building}
            label="Median rent"
            value={fmtMoney(m.median_rent)}
            sub="per month"
            testid="stat-median-rent"
          />
        ) : null}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[var(--ink-2)]">
          <Icon size={15} />
        </div>
        <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">{label}</div>
      </div>
      <div className="font-display font-extrabold text-2xl tracking-tight tabular-nums">{value}</div>
      <div className="text-[11px] text-[var(--ink-3)] mt-1">{sub}</div>
    </div>
  );
}
